import React from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Link, Briefcase, Eye, FileText, Image } from 'lucide-react';
import { LinkedInContent } from '../types/socialMedia.types';
import CharacterCounter from '../components/CharacterCounter';
import ScheduleSettings from '../components/ScheduleSettings';
import MediaUploader from '../components/MediaUploader';
import { PLATFORMS } from '../config/platformConfig';

interface LinkedInEditorProps {
  content: LinkedInContent;
  isEditing: boolean;
  onChange: (field: keyof LinkedInContent, value: any) => void;
}

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ]
};

export default function LinkedInEditor({ content, isEditing, onChange }: LinkedInEditorProps) {
  const platform = PLATFORMS.linkedin;
  const characterCount = content.text ? content.text.replace(/<[^>]*>/g, '').length : 0;
  const isArticle = content.postType === 'Article';
  const characterLimit = isArticle ? platform.limits.article! : platform.limits.text!;

  return (
    <div className="space-y-4">
      {/* Post Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Post Type</label>
        <select
          value={content.postType}
          onChange={(e) => onChange('postType', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100"
        >
          {platform.postTypes.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
      </div>

      {/* Headline (for Articles) */}
      {isArticle && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <FileText className="inline w-4 h-4 mr-1" />
            Article Headline
          </label>
          <input
            type="text"
            value={content.headline}
            onChange={(e) => onChange('headline', e.target.value)}
            disabled={!isEditing}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100"
            placeholder="Article headline..."
            maxLength={100}
          />
          <CharacterCounter
            current={content.headline.length}
            limit={100}
            label="Headline"
            showProgressBar={false}
          />
        </div>
      )}

      {/* Post/Article Text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {isArticle ? 'Article Content' : 'Post Text'}
        </label>
        {isEditing ? (
          <ReactQuill
            value={content.text || ''}
            onChange={(value) => onChange('text', value)}
            modules={quillModules}
            className="bg-white rounded"
            style={{ minHeight: isArticle ? '300px' : '150px' }}
            placeholder={isArticle ? 'Write your article...' : 'Share an update...'}
          />
        ) : (
          <div
            className={`px-3 py-2 bg-gray-50 rounded ${isArticle ? 'min-h-[300px]' : 'min-h-[100px]'}`}
            dangerouslySetInnerHTML={{
              __html: content.text || '<span class="text-gray-400">No content</span>'
            }}
          />
        )}
        <div className="mt-2">
          <CharacterCounter
            current={characterCount}
            limit={characterLimit}
            label={isArticle ? 'Article content' : 'Post text'}
          />
        </div>
        {isArticle && (
          <p className="text-xs text-gray-500 mt-1">
            LinkedIn articles can be up to 110,000 characters with rich formatting
          </p>
        )}
      </div>

      {/* Media Upload */}
      {(content.postType === 'Image' || content.postType === 'Video' || content.postType === 'Document') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Image className="inline w-4 h-4 mr-1" />
            Media Files
          </label>
          <MediaUploader
            media={content.media || []}
            onChange={(media) => onChange('media', media)}
            disabled={!isEditing}
            maxFiles={content.postType === 'Document' ? 5 : 1}
            acceptedTypes={content.postType === 'Document' ? '.pdf,.doc,.docx,.ppt,.pptx' : content.postType === 'Video' ? 'video/*' : 'image/*'}
            platformName="LinkedIn"
          />
        </div>
      )}

      {/* Link */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Link className="inline w-4 h-4 mr-1" />
          Link URL
        </label>
        <input
          type="url"
          value={content.link}
          onChange={(e) => onChange('link', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100"
          placeholder="https://example.com"
        />
      </div>

      {/* Visibility */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Eye className="inline w-4 h-4 mr-1" />
          Visibility
        </label>
        <select
          value={content.visibility}
          onChange={(e) => onChange('visibility', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100"
        >
          <option value="Public">Public - Anyone on or off LinkedIn</option>
          <option value="Connections">Connections Only</option>
        </select>
      </div>

      {/* Professional Targeting */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <Briefcase className="inline w-4 h-4 mr-1" />
          Professional Targeting
        </label>
        <input
          type="text"
          value={content.targeting}
          onChange={(e) => onChange('targeting', e.target.value)}
          disabled={!isEditing}
          className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 disabled:bg-gray-100"
          placeholder="e.g., Technology, Marketing Professionals, C-Suite"
        />
        <p className="text-xs text-gray-500 mt-1">
          Target specific industries, job titles, or professional interests
        </p>
      </div>

      {/* Schedule */}
      <ScheduleSettings
        scheduledDate={content.scheduledDate}
        scheduledTime={content.scheduledTime}
        onDateChange={(date) => onChange('scheduledDate', date)}
        onTimeChange={(time) => onChange('scheduledTime', time)}
        disabled={!isEditing}
        platformName="LinkedIn"
      />
    </div>
  );
}